import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  alpha,
  Alert,
  List,
  ListItemButton,
  InputAdornment,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useProductSearch } from '../../hooks/useProductSearch';
import { useLinkBarcode } from '../../hooks/useBarcodeProductSearch';
import type { Product } from '../../types';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

// Default colors for non-POS contexts
const defaultColors: POSColors = {
  bg: '#F8FAFC',
  surface: '#FFFFFF',
  surfaceLight: '#F1F5F9',
  border: '#E2E8F0',
  textPrimary: '#1E293B',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  accent: '#2563EB',
  accentLight: '#3B82F6',
};

interface BarcodeLinkModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** The barcode to link to a product */
  barcode: string;
  /** Callback when barcode is successfully linked to a product */
  onLinked: (product: Product) => void;
  /** Optional theme colors (for POS context) */
  colors?: POSColors;
}

/**
 * Modal for searching and selecting an existing product to link a barcode to.
 * Users can search by product name or SKU, then select a product to link.
 */
export const BarcodeLinkModal: React.FC<BarcodeLinkModalProps> = ({
  open,
  onClose,
  barcode,
  onLinked,
  colors = defaultColors,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  // Debounced search query for API calls
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search for products
  const { data: searchResults, isLoading: isSearching } = useProductSearch({
    q: debouncedQuery,
    limit: 10,
  });

  // Link barcode mutation
  const linkBarcodeMutation = useLinkBarcode();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setDebouncedQuery('');
      setSelectedProduct(null);
      setApiError(null);
    }
  }, [open]);

  // Filter out products that already have a barcode
  const availableProducts = useMemo(() => {
    if (!searchResults?.data) return [];
    return searchResults.data.filter(
      (product) => !product.barcode || product.barcode.trim() === ''
    );
  }, [searchResults]);

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    setApiError(null);
  };

  const handleLink = async () => {
    if (!selectedProduct) return;

    setApiError(null);

    try {
      const updatedProduct = await linkBarcodeMutation.mutateAsync({
        productId: selectedProduct.id,
        barcode,
      });
      onLinked(updatedProduct);
      // Note: Don't call onClose() - the parent's onLinked callback handles dialog closure
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Impossible de lier le code-barres au produit';
      setApiError(errorMessage);
    }
  };

  const isLinking = linkBarcodeMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: alpha(colors.accent, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LinkIcon sx={{ color: colors.accent, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              color: colors.textPrimary,
            }}
          >
            Associer le code-barres
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              color: colors.textMuted,
              fontFamily: 'monospace',
            }}
          >
            {barcode}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {apiError && (
          <Alert
            severity="error"
            sx={{
              backgroundColor: alpha('#EF4444', 0.1),
              color: colors.textPrimary,
              '& .MuiAlert-icon': {
                color: '#EF4444',
              },
            }}
          >
            {apiError}
          </Alert>
        )}

        {/* Search input */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher un produit par nom ou SKU..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isSearching ? (
                  <CircularProgress size={18} sx={{ color: colors.textMuted }} />
                ) : (
                  <SearchIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
                )}
              </InputAdornment>
            ),
          }}
          sx={getTextFieldStyles(colors)}
        />

        {/* Search results */}
        <Box
          sx={{
            flex: 1,
            minHeight: 200,
            maxHeight: 300,
            overflowY: 'auto',
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            backgroundColor: colors.surfaceLight,
          }}
        >
          {!debouncedQuery && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                py: 4,
                color: colors.textMuted,
              }}
            >
              <SearchIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
              <Typography sx={{ fontSize: '14px' }}>
                Recherchez un produit pour l'associer
              </Typography>
            </Box>
          )}

          {debouncedQuery && !isSearching && availableProducts.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                py: 4,
                color: colors.textMuted,
              }}
            >
              <InventoryIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
              <Typography sx={{ fontSize: '14px', textAlign: 'center' }}>
                Aucun produit sans code-barres trouvé
              </Typography>
              <Typography sx={{ fontSize: '12px', mt: 0.5, textAlign: 'center' }}>
                Les produits avec un code-barres existant sont exclus
              </Typography>
            </Box>
          )}

          {availableProducts.length > 0 && (
            <List disablePadding>
              {availableProducts.map((product) => {
                const isSelected = selectedProduct?.id === product.id;
                return (
                  <ListItemButton
                    key={product.id}
                    onClick={() => handleProductSelect(product)}
                    selected={isSelected}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderBottom: `1px solid ${colors.border}`,
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                      '&.Mui-selected': {
                        backgroundColor: alpha(colors.accent, 0.1),
                        '&:hover': {
                          backgroundColor: alpha(colors.accent, 0.15),
                        },
                      },
                      '&:hover': {
                        backgroundColor: alpha(colors.accent, 0.05),
                      },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: colors.textPrimary,
                          }}
                        >
                          {product.name}
                        </Typography>
                        {isSelected && (
                          <CheckCircleIcon
                            sx={{ fontSize: 18, color: colors.accent }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mt: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: colors.textMuted,
                            fontFamily: 'monospace',
                          }}
                        >
                          SKU: {product.sku}
                        </Typography>
                        {product.category && (
                          <Typography
                            sx={{
                              fontSize: '12px',
                              color: colors.textMuted,
                            }}
                          >
                            • {product.category}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: colors.textSecondary,
                            fontWeight: 500,
                          }}
                        >
                          {product.price.toLocaleString('fr-DZ', {
                            style: 'currency',
                            currency: 'DZD',
                            minimumFractionDigits: 0,
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>

        {/* Selected product confirmation */}
        {selectedProduct && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              backgroundColor: alpha(colors.accent, 0.08),
              borderRadius: '10px',
              border: `1px solid ${alpha(colors.accent, 0.2)}`,
            }}
          >
            <CheckCircleIcon sx={{ color: colors.accent, fontSize: 20 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: colors.textSecondary,
                }}
              >
                Le code-barres sera associé à :
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.textPrimary,
                }}
              >
                {selectedProduct.name}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLinking}
          sx={{
            borderColor: colors.border,
            color: colors.textSecondary,
            '&:hover': {
              borderColor: colors.textMuted,
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          Annuler
        </Button>
        <Button
          onClick={handleLink}
          variant="contained"
          disabled={!selectedProduct || isLinking}
          sx={{
            backgroundColor: colors.accent,
            '&:hover': {
              backgroundColor: colors.accentLight,
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(colors.accent, 0.5),
            },
          }}
        >
          {isLinking ? (
            <CircularProgress size={20} sx={{ color: '#FFF' }} />
          ) : (
            'Associer'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function for text field styles
const getTextFieldStyles = (colors: POSColors) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.surfaceLight,
    borderRadius: '10px',
    fontSize: '14px',
    '& fieldset': {
      borderColor: colors.border,
    },
    '&:hover fieldset': {
      borderColor: colors.textMuted,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.accent,
    },
  },
  '& .MuiOutlinedInput-input': {
    color: colors.textPrimary,
    '&::placeholder': {
      color: colors.textMuted,
      opacity: 1,
    },
  },
});

export default BarcodeLinkModal;
